import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Search } from 'lucide-react';
import { api } from '../../lib/api';
import { FotoProduto } from '../../components/FotoProduto';

// Busca de produto por codigo ou descricao, pra Venda Perdida e pro Achado da auditoria ([DA-868]).
//
// ⚠️ O resultado NAO traz quantidade de estoque de proposito: no Achado a contagem e' cega, e na
// Venda Perdida o numero do sistema e' gravado pelo servidor no instante do registro — mostrar aqui
// convidaria a vendedora a "conferir" antes, quando o que importa e' o que ELA viu na prateleira.
//
// A lista abre EMBAIXO do campo, no fluxo da pagina (nao `absolute`): nenhuma moldura com
// `overflow` consegue corta-la, e no tablet ela empurra o conteudo em vez de cobrir.

export interface ProdutoBuscado {
  id: number;
  /** So a busca da Venda Perdida manda ([DA-872]): `novo_produto` = item da prateleira Novos Produtos. */
  tipo?: 'produto' | 'novo_produto';
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  unidade: string;
  fora_da_auditoria: boolean;
  lugares: { localizacao_id: number; codigo: string; descricao: string | null; principal: boolean }[];
}

export function BuscaProdutoEstoque({
  endpoint, onEscolher, bloquearForaDaAuditoria = false, autoFocus = false, onSemCadastro,
  grande = false, placeholder = 'Digite o codigo ou parte da descricao...', rotuloSemCadastro = 'Produto sem cadastro', acaoAoLado,
}: {
  endpoint: string;
  onEscolher: (p: ProdutoBuscado) => void;
  /** No Achado, produto vendido por MT/RL/KG aparece cinza com o motivo (fica fora, decisao dele). */
  bloquearForaDaAuditoria?: boolean;
  autoFocus?: boolean;
  /** Venda Perdida ([DA-872]): ultima linha da lista registra o texto digitado como produto SEM cadastro.
   *  Fica sempre la (nao so com a lista vazia) — o parecido que aparece pode nao ser o que o cliente pediu. */
  onSemCadastro?: (texto: string) => void;
  /** Venda Perdida simplificada (pedido dele, 31/10/2026: operador com pouca pratica): campo maior. */
  grande?: boolean;
  placeholder?: string;
  /** Nome da ultima linha da lista (a do `onSemCadastro`). */
  rotuloSemCadastro?: string;
  /** Botao na MESMA linha do campo, com a altura dele (ex.: "+ Novo Produto"). */
  acaoAoLado?: ReactNode;
}) {
  const [termo, setTermo] = useState('');
  const [lista, setLista] = useState<ProdutoBuscado[]>([]);
  const [buscando, setBuscando] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    const t = termo.trim();
    if (t.length < 2) { setLista([]); return; }
    const req = ++reqId.current;
    setBuscando(true);
    const h = setTimeout(() => {
      api.get<ProdutoBuscado[]>(`${endpoint}?termo=${encodeURIComponent(t)}`)
        .then((r) => { if (req === reqId.current) setLista(r); })
        .catch(() => { if (req === reqId.current) setLista([]); })
        .finally(() => { if (req === reqId.current) setBuscando(false); });
    }, 250);
    return () => clearTimeout(h);
  }, [termo, endpoint]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <Search size={grande ? 20 : 16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }} />
          <input
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="pl-9"
            style={grande
              ? { width: '100%', fontSize: 16, padding: '14px 16px 14px 42px' }
              : { width: '100%', fontSize: 15, padding: '10px 12px 10px 36px' }}
          />
        </div>
        {acaoAoLado}
      </div>
      {termo.trim().length >= 2 && (
        <div style={{ border: '1px solid var(--panel-border)', borderRadius: 6, marginTop: 4, background: 'var(--panel-bg)', maxHeight: 320, overflowY: 'auto' }}>
          {buscando && lista.length === 0 && <div className="muted" style={{ padding: 10, fontSize: 13 }}>Buscando...</div>}
          {!buscando && lista.length === 0 && (
            <div className="muted" style={{ padding: 10, fontSize: 13 }}>
              {onSemCadastro ? 'Nenhum produto nem Novo Produto com esse codigo ou descricao.' : 'Nenhum produto ativo com esse codigo ou descricao.'}
            </div>
          )}
          {lista.map((p) => {
            const bloqueado = bloquearForaDaAuditoria && p.fora_da_auditoria;
            const ehNovo = p.tipo === 'novo_produto';
            return (
              <button
                key={`${p.tipo ?? 'produto'}-${p.id}`}
                type="button"
                disabled={bloqueado}
                onClick={() => { onEscolher(p); setTermo(''); setLista([]); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                  padding: '8px 10px', border: 'none', borderBottom: '1px solid var(--page-bg)',
                  background: 'transparent', cursor: bloqueado ? 'not-allowed' : 'pointer', opacity: bloqueado ? 0.5 : 1,
                  color: 'inherit',
                }}
              >
                {p.foto_url ? <FotoProduto url={p.foto_url} tam={36} /> : <div className="produto-thumb-vazio" style={{ width: 36, height: 36, flex: '0 0 auto' }} />}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="text-gray-500 font-bold no-underline" style={{ fontSize: 12 }}>{p.codigo_interno}</span>{' '}
                  <span style={{ fontWeight: 600 }}>{p.descricao}</span>
                  <span className="muted" style={{ fontSize: 12, display: 'block' }}>
                    {ehNovo ? `Novos Produtos · ${p.unidade}` : <>{p.lugares.map((l) => l.codigo).join(' · ') || 'sem prateleira'} · {p.unidade}</>}
                    {bloqueado && ` — vendido por ${p.unidade}, fica fora da auditoria`}
                  </span>
                </span>
              </button>
            );
          })}
          {onSemCadastro && !buscando && (
            <button
              type="button"
              onClick={() => { onSemCadastro(termo.trim().toUpperCase()); setTermo(''); setLista([]); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 10px', border: 'none',
                background: 'transparent', cursor: 'pointer', color: 'var(--primary)', fontWeight: 700, fontSize: 14,
              }}
            >
              + {rotuloSemCadastro}: "{termo.trim().toUpperCase()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
